import { Radio } from 'antd';
import styled from 'styled-components';

/**
 * Shell do módulo Interoperabilidade.
 *
 * As regras `.ant-tabs-*` vivem aqui porque o `createGlobalStyle` do repo não
 * cobre as abas: cada tela que usa `Tabs` as tinge localmente — StationConfigForm,
 * VirtualGroupPage, PushNotificationConfigForm e StationEditPage fazem exatamente
 * este bloco com `theme.primary`. Sem ele as abas saem no azul padrão do antd, que
 * não é a cor de nenhum tenant.
 */
export const Page = styled.div`
  padding: 16px 24px;

  .ant-tabs {
    width: 100%;
  }

  .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
    color: ${({ theme }) => theme.primary};
  }

  .ant-tabs-tab {
    &:hover,
    &:active,
    &:focus {
      color: ${({ theme }) => theme.primary};
    }
  }

  .ant-tabs-ink-bar {
    background: ${({ theme }) => theme.primary};
  }

  /*
    A última aba — API — fica separada das outras, à direita.

    As cinco primeiras são operação do dia: conexões, convites, mapa, transações, histórico.
    A API é configuração da conta, para quem vai integrar um sistema, e não pertence à mesma
    sequência. Empurrar com margin-left auto mantém a aba nativa do antd, com o mesmo
    comportamento de teclado e a mesma barra de tinta — separar reconstruindo o Tabs custaria
    isso.
  */
  .ant-tabs-nav-list .ant-tabs-tab:last-of-type {
    margin-left: auto;
    padding-left: 24px;
    border-left: 1px solid #e9ebee;
  }
`;

export const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 16px;
`;

export const Title = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 600;
  font-size: 18px;
  line-height: 1.2;
  color: #12263f;
`;

export const Subtitle = styled.p`
  margin: 6px 0 0;
  color: #5b6472;
  font-size: 12.5px;
  max-width: 620px;
`;

export const Actions = styled.div`
  display: flex;
  gap: 8px;
  flex: none;
`;

export const PrimaryButton = styled.button`
  background: ${({ theme }) => theme.primary};
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 9px 16px;
  cursor: pointer;
`;

export const SecondaryButton = styled.button`
  background: #fff;
  color: #4a5462;
  border: 1px solid #d9dce1;
  border-radius: 6px;
  padding: 9px 16px;
  cursor: pointer;
`;

/**
 * Barra de filtros das listagens do módulo.
 *
 * Estava duplicada, idêntica, em Transações e Histórico. Como agora Conexões, Convites e
 * Integrações também filtram, virou peça — cinco cópias do mesmo bloco divergiriam na
 * primeira mudança de espaçamento.
 */
export const FilterBar = styled.div`
  background: #fff;
  border-radius: 3px;
  box-shadow: 0px 1px 3px 0px rgba(0, 0, 0, 0.21);
  padding: 10px 14px;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

/** Empurra o que vem depois para a direita da barra. */
export const FilterSpacer = styled.div`
  flex: 1;
`;

/**
 * Quantos resultados o filtro deixou.
 *
 * Existe porque filtro sem contagem esconde o próprio efeito: quem filtra e vê pouca coisa
 * precisa saber se é porque há pouca coisa, ou porque o filtro cortou.
 */
export const FilterCount = styled.span`
  color: #8d929e;
  font-size: 12px;
  white-space: nowrap;
`;

/**
 * Peças dos painéis de detalhe (MV2-1706).
 *
 * Toda listagem do módulo abre um detalhe, e cada painel repetia as mesmas seis
 * declarações. Ficam aqui para os painéis novos não nascerem cada um com o seu
 * espaçamento.
 */
export const DetailCard = styled.div`
  background: #fff;
  border: 1px solid #e9ebee;
  border-radius: 8px;
  padding: 14px 16px;
  margin-bottom: 10px;
`;

export const DetailTitle = styled.h4`
  margin: 0 0 6px;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #8a9099;
`;

export const DetailRow = styled.p`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin: 0;
  padding: 6px 0;
  border-bottom: 1px solid #f2f4f6;
  font-size: 12.5px;
  color: #6b7280;

  &:last-of-type {
    border-bottom: none;
  }
`;

export const DetailValue = styled.span`
  color: #2d3339;
  text-align: right;
`;

/** Id, IP, código: o que a pessoa vai copiar para pedir a correção. */
export const DetailMono = styled.span`
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
  word-break: break-all;
`;

export const DetailHint = styled.p`
  margin: 8px 0 0;
  font-size: 11.5px;
  color: #8a9099;
`;

/**
 * Tabela cujas linhas abrem o detalhe no clique (MV2-1706).
 *
 * O módulo abre detalhe clicando na linha — é como Conexões e API já funcionavam. As
 * abas novas nasceram com um botão "Detalhes" na última coluna, o que dava dois jeitos
 * de fazer a mesma coisa no mesmo módulo.
 *
 * O cursor não é enfeite: sem ele a linha não avisa que é clicável, e o detalhe fica
 * escondido de quem não tenta.
 */
export const ClickableRows = styled.div`
  .interop-row {
    cursor: pointer;
  }
`;

/**
 * A seta no fim da linha que abre detalhe (MV2-1706).
 *
 * Não é enfeite: é o que diz que a linha é clicável. O cursor só aparece depois de a
 * pessoa passar o mouse por cima — a seta avisa antes, e é como o financeiro, Conexões
 * e API já faziam.
 */
export const Chevron = styled.span`
  display: flex;
  justify-content: flex-end;
  color: #757b88;
`;

/**
 * Alternador de duas vias (Lista/Mapa, Pinos/Calor) na cor do tenant.
 *
 * O Radio.Group com buttonStyle="solid" sai no azul padrão do antd, que não é a cor de
 * nenhum tenant — o resto do módulo tinge estado marcado com theme.primary.
 *
 * A interpolação fica fora de apóstrofos de propósito: dentro deles o styled-components
 * não a avalia, a declaração é descartada e o botão marcado fica sem fundo — foi o
 * defeito que deixou onze estados marcados do módulo sem cor nenhuma.
 */
export const ViewSwitch = styled(Radio.Group)`
  .ant-radio-button-wrapper {
    font-size: 11.5px;
  }

  .ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled) {
    background: ${({ theme }) => theme.primary};
    border-color: ${({ theme }) => theme.primary};
    color: #fff;
  }

  /* A borda entre os dois botões, que o antd pinta com a cor do tema dele. */
  .ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled)::before {
    background-color: ${({ theme }) => theme.primary};
  }

  .ant-radio-button-wrapper:hover {
    color: ${({ theme }) => theme.primary};
  }

  .ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled):hover {
    background: ${({ theme }) => theme.primary};
    border-color: ${({ theme }) => theme.primary};
    color: #fff;
    opacity: 0.9;
  }
`;

/**
 * Faixa de cartões de total, no alto da tela (MV2-1706).
 *
 * Nasceu na aba Estações e virou compartilhada quando Transações pediu o mesmo: número que
 * resume a tela se lê antes da tabela, e não depois dela. No rodapé, o total só aparecia para
 * quem rolava até o fim — e em tela com dez linhas isso é rolagem que ninguém faz de propósito.
 */
export const TotalsStrip = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 8px;
`;

export const TotalCard = styled.div`
  flex: 1 1 150px;
  background: #fff;
  border: 1px solid #e9ebee;
  border-radius: 8px;
  padding: 10px 14px;
`;

export const TotalCardKey = styled.span`
  display: block;
  font-size: 11px;
  color: #8a9099;
`;

export const TotalCardValue = styled.strong`
  font-size: 18px;
  color: #2d3339;
  font-variant-numeric: tabular-nums;
`;

/**
 * O cartão branco que envolve a tabela de uma aba (MV2-1706).
 *
 * Estava declarado, palavra por palavra igual, em quatro abas — Convites, Estações, Transações e
 * Auditoria. Um ajuste de raio ou de borda exigia quatro edições, e bastava esquecer uma para uma
 * aba ficar diferente das outras.
 *
 * A aba API não usa este: ela usa sombra em vez de borda, como a barra de filtro. Deixei como
 * está — igualar mudaria a aparência dela, e isso é decisão de quem desenha, não de quem arruma.
 */
export const ListCard = styled.div`
  background: #fff;
  border: 1px solid #e9ebee;
  border-radius: 8px;
  overflow: hidden;
`;

/**
 * A segunda linha de uma célula: conta, alias, contagem — o detalhe abaixo do nome.
 *
 * Consolidado onde as declarações eram idênticas (Auditoria, Convites, Estações). Conexões tem
 * uma variante sem `margin-top` e a API usa outra cor e outro tamanho: ficaram locais, porque
 * uniformizá-las mudaria a tela, e isso não é arrumação.
 */
export const CellSub = styled.div`
  color: #8a9099;
  font-size: 11px;
  margin-top: 2px;
`;

/**
 * Entrada permanente para o tutorial, no cabeçalho (MV2-1791).
 *
 * Link, e não um terceiro botão sólido: o cabeçalho já tem duas ações do dia — "Inserir token" e
 * "Convidar parceiro" — e um botão de mesmo peso ao lado delas disputaria a atenção com aquilo
 * que a pessoa vem fazer. Fica na cor do tenant como o resto do estado marcado do módulo.
 */
export const TourLink = styled.button`
  align-self: center;
  background: none;
  border: none;
  padding: 9px 4px;
  color: ${({ theme }) => theme.primary};
  font-size: 12.5px;
  cursor: pointer;
  text-decoration: underline;
  white-space: nowrap;
`;
